/**
 * Une tâche dans la liste (refonte v4 du 18/10/2026) : ce qu'il faut faire,
 * pour quand, pour qui ; « Fait » est le geste de la ligne, le reste (bloquer,
 * débloquer, rouvrir, supprimer) passe par son menu « … », jamais par une
 * corbeille posée sur chaque ligne. Le titre ouvre la page de la tâche (faite
 * comprise : elle s'y relit, photo de preuve comprise).
 *
 * Objectif (décision du 15/10/2026) : « Fait » demande une photo (appareil du
 * téléphone), analysée par l'antivirus avant l'enregistrement ; une photo déjà
 * envoyée est gardée pour réessayer sans la reprendre. « Je bloque » prévient
 * celui qui l'a donné et propose de l'appeler.
 */
import { useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, Camera, Check, CheckCircle2, ListTodo, Phone, RotateCcw, Target, Trash2 } from 'lucide-react';
import { OBJECTIVE_LIMITS, OBJECTIVE_PROOF_FILE_KIND } from '@myequip/shared';
import { api, ApiError, ScanPendingError, uploadFile, waitForScan } from '../../core/api.js';
import { dateFr } from '../../core/format.js';
import { t } from '../../core/i18n.js';
import { Button, ConfirmActionModal } from '../../components/ui.js';
import { Badge, BadgeGroup } from '../../components/ui/Badge.js';
import { CallButton } from '../../components/CallButton.js';
import { RecordIcon, RecordRow } from '../../components/RecordRow.js';
import type { ActionMenuItem } from '../../components/action-menu.js';
import { OBJECTIVE_STATE_LABELS } from './objective.js';
import { isLate, stateOf, whenLine, whoLine, type Task } from './task-model.js';

const PROOF_STEPS = {
  envoi: 'taches.preuve.envoi',
  analyse: 'taches.preuve.analyse',
  enregistrement: 'taches.preuve.enregistrement',
} as const;

const STATE_TONE = { en_cours: 'info', bloque: 'warn', tenu: 'ok', fait_apres: 'ok', non_atteint: 'danger' } as const;

/** Pastilles d'une tâche : l'état d'un objectif, une priorité qui sort de l'ordinaire. */
export function TaskBadges({ task }: { task: Task }): ReactNode {
  const state = stateOf(task);
  const priority = task.priority === 'haute' || task.priority === 'urgente';
  if (!state && !priority) return null;
  return (
    <BadgeGroup>
      {state ? <Badge tone={STATE_TONE[state]}>{t('taches.objectifEtat', { etat: OBJECTIVE_STATE_LABELS[state] })}</Badge> : null}
      {priority ? <Badge tone={task.priority === 'urgente' ? 'danger' : 'warn'}>{t(`taches.priorites.${task.priority}`)}</Badge> : null}
    </BadgeGroup>
  );
}

export function TaskRow(props: {
  task: Task;
  meId: string | undefined;
  /** Adresse de la liste, pour revenir au même onglet et à la même recherche. */
  from: string;
  focused?: boolean;
  onChanged: (message?: string) => void;
  /** Un geste refusé ou impossible : la liste le dit dans un bandeau. */
  onError: (message: string) => void;
}): ReactNode {
  const { task, meId } = props;
  const report = (error: unknown): void => props.onError((error as Error).message);
  const mine = task.assignee_id === meId;
  const author = task.created_by === meId;
  const done = task.status === 'faite';
  const objective = task.due_at !== null;
  const blocked = objective && !done && task.blocked_at !== null;
  const [confirm, setConfirm] = useState<'delete' | 'block'>();

  const setStatus = useMutation({
    mutationFn: (status: Task['status']) => api(`/tasks/${task.id}/status`, { method: 'POST', body: { status } }),
    onSuccess: (_result, status) => props.onChanged(status === 'faite' ? t('taches.faite', { titre: task.title }) : t('taches.rouverte')),
    onError: report,
  });
  const remove = useMutation({
    mutationFn: () => api(`/tasks/${task.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setConfirm(undefined);
      props.onChanged(t('taches.supprimee'));
    },
  });
  const setBlocked = useMutation({
    mutationFn: (input: { blocked: boolean; reason?: string | undefined }) =>
      api(`/tasks/${task.id}/blocked`, {
        method: 'POST',
        body: input.blocked ? { blocked: true, ...(input.reason ? { reason: input.reason } : {}) } : { blocked: false },
      }),
    onSuccess: (_result, input) => {
      setConfirm(undefined);
      props.onChanged(input.blocked ? t('taches.blocage.prevenu', { nom: task.created_by_name }) : t('taches.blocage.leve'));
    },
    // Le refus d'un blocage se lit dans sa fenêtre ; celui d'un déblocage, dans la liste.
    onError: (error, input) => {
      if (!input.blocked) report(error);
    },
  });

  // « Fait » d'un objectif : photo, antivirus, enregistrement.
  const photoInput = useRef<HTMLInputElement>(null);
  const [proofStep, setProofStep] = useState<keyof typeof PROOF_STEPS>();
  const [sentPhoto, setSentPhoto] = useState<string>();
  const finish = useMutation({
    mutationFn: async (file?: File) => {
      let fileId = sentPhoto;
      if (file) {
        setProofStep('envoi');
        fileId = (await uploadFile(file, OBJECTIVE_PROOF_FILE_KIND)).fileId;
        setSentPhoto(fileId);
      }
      if (!fileId) throw new Error(t('taches.preuve.manquante'));
      setProofStep('analyse');
      await waitForScan(fileId);
      setProofStep('enregistrement');
      await api(`/tasks/${task.id}/status`, { method: 'POST', body: { status: 'faite', proofFileId: fileId } });
    },
    onSuccess: () => {
      setSentPhoto(undefined);
      props.onChanged(t('taches.faite', { titre: task.title }));
    },
    // Une photo refusée (antivirus, preuve rejetée) échouerait à chaque essai : seules une
    // coupure réseau ou une analyse encore en cours la gardent ; sinon « Fait » rouvre l'appareil.
    onError: (err) => {
      const retryable = err instanceof ScanPendingError || (err instanceof ApiError && err.code === 'network_error');
      if (!retryable) setSentPhoto(undefined);
      report(err instanceof ApiError && err.code === 'network_error' ? new Error(t('taches.preuve.nonEnvoyee')) : err);
    },
    onSettled: () => setProofStep(undefined),
  });
  const onPhoto = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) finish.mutate(file);
  };
  const retryPhoto = Boolean(sentPhoto && finish.isError);

  const menu: ActionMenuItem[] = [
    ...(mine && !author && objective && !done && !blocked
      ? [{ label: t('taches.blocage.jeBloque'), icon: <AlertTriangle size={18} aria-hidden="true" />, onSelect: () => { setBlocked.reset(); setConfirm('block'); } }]
      : []),
    ...(blocked
      ? [{ label: mine ? t('taches.blocage.jeNeBloquePlus') : t('taches.blocage.debloque'), icon: <Check size={18} aria-hidden="true" />, onSelect: () => setBlocked.mutate({ blocked: false }) }]
      : []),
    ...(done ? [{ label: t('taches.rouvrir'), icon: <RotateCcw size={18} aria-hidden="true" />, onSelect: () => setStatus.mutate('a_faire') }] : []),
    ...(author
      ? [{ label: t('taches.supprimer'), tone: 'danger' as const, icon: <Trash2 size={18} aria-hidden="true" />, onSelect: () => { remove.reset(); setConfirm('delete'); } }]
      : []),
  ];

  const lines = [
    whenLine(task),
    whoLine(task, meId),
    objective && !done && task.done_criteria ? t('taches.finiQuand', { texte: task.done_criteria }) : undefined,
    done && task.done_at ? t('taches.faiteLe', { date: dateFr(task.done_at) }) : undefined,
    proofStep ? <span role="status">{t(PROOF_STEPS[proofStep])}</span> : undefined,
  ].filter(Boolean) as ReactNode[];
  const next = blocked
    ? mine && !author
      ? t('taches.blocage.vousBloquez', { nom: task.created_by_name })
      : t('taches.blocage.bloque', { nom: task.assignee_name, raison: task.blocked_reason ? ` : ${task.blocked_reason}` : '' })
    : undefined;

  const doneButton = done ? null : objective ? (
    <Button
      variant="secondary"
      disabled={finish.isPending}
      ariaLabel={t('taches.faitPhotoAria', { titre: task.title })}
      onClick={() => (retryPhoto ? finish.mutate(undefined) : photoInput.current?.click())}
    >
      <Camera size={18} aria-hidden="true" /> {finish.isPending ? t('taches.enCours') : retryPhoto ? t('taches.reessayer') : t('taches.fait')}
    </Button>
  ) : (
    <Button variant="secondary" disabled={setStatus.isPending} ariaLabel={t('taches.faitAria', { titre: task.title })}
      onClick={() => setStatus.mutate('faite')}>
      <Check size={18} aria-hidden="true" /> {setStatus.isPending ? t('taches.enCours') : t('taches.fait')}
    </Button>
  );
  const call = blocked && mine && !author && task.created_by_phone ? (
    <CallButton phone={task.created_by_phone} who={task.created_by_name} variant="primary">
      <Phone size={18} aria-hidden="true" /> {t('taches.blocage.appeler', { nom: task.created_by_name })}
    </CallButton>
  ) : null;

  return (
    <>
      <RecordRow
        to={`/taches/${task.id}/modifier`}
        state={{ from: props.from }}
        {...(props.focused ? { className: 'record-row--focused' } : {})}
        urgent={!done && (task.priority === 'urgente' || isLate(task) || stateOf(task) === 'non_atteint')}
        lead={<RecordIcon>{done ? <CheckCircle2 size={20} /> : blocked ? <AlertTriangle size={20} /> : objective ? <Target size={20} /> : <ListTodo size={20} />}</RecordIcon>}
        title={task.title}
        lines={lines}
        next={next}
        badge={<TaskBadges task={task} />}
        menu={menu}
        actions={doneButton || call ? <>{call}{doneButton}</> : undefined}
      />
      {/* Appareil photo arrière sur téléphone ; choix d'un fichier sur ordinateur. */}
      {objective && !done ? (
        <input ref={photoInput} type="file" accept="image/*" capture="environment" hidden onChange={onPhoto} />
      ) : null}
      <ConfirmActionModal
        open={confirm === 'delete'}
        title={t('taches.supprimerTitre')}
        consequence={mine ? t('taches.supprimerSienne') : t('taches.supprimerDonnee', { nom: task.assignee_name })}
        confirmLabel={t('taches.supprimer')}
        tone="danger"
        pending={remove.isPending}
        error={remove.error ? (remove.error as Error).message : undefined}
        onClose={() => setConfirm(undefined)}
        onConfirm={() => remove.mutate()}
      />
      <ConfirmActionModal
        open={confirm === 'block'}
        title={t('taches.blocage.titre')}
        consequence={t('taches.blocage.consequence', { nom: task.created_by_name })}
        confirmLabel={t('taches.blocage.prevenir', { nom: task.created_by_name })}
        reason={{ label: t('taches.blocage.raison'), placeholder: t('taches.blocage.raisonExemple'), minLength: 0 }}
        pending={setBlocked.isPending}
        error={setBlocked.error ? (setBlocked.error as Error).message : undefined}
        onClose={() => setConfirm(undefined)}
        onConfirm={(reason) => setBlocked.mutate({ blocked: true, reason: reason?.slice(0, OBJECTIVE_LIMITS.blockedReasonMax) })}
      />
    </>
  );
}
